"use client";

import { CheckCircle, Loader2 } from "lucide-react";

import { Comedian } from "@/data/comedians";
import { ShowWithComedian } from "@/data/shows";
import { WaitlistInlineForm } from "@punchup/ui-common/components/waitlist";
import { useWaitlistStatus } from "@punchup/venues/client";

interface EventWaitlistCtaProps {
  show: ShowWithComedian;
  comedian: Comedian;
}

/**
 * The waitlist CTA for the embedded event page (`/e/[eventId]`).
 *
 * The join is always attributed to the COMEDIAN (`ownerId={comedian.id}`,
 * `isVenue={false}`) — the embedded page previously preferred `show.venue_id`,
 * which landed the fan in the venue's waitlist group instead of the comedian's
 * (PUN-5019). Membership is read via the same `useWaitlistStatus` hook the
 * comedian/venue surfaces use, so an already-joined viewer sees the joined
 * state instead of the join form on refresh.
 *
 * Deliberately INLINE — no modal/Dialog. `/e/` is embedded in comedian sites
 * via a cross-origin iframe, where an overlay can only cover the embed
 * rectangle (not the host page) and reads as broken.
 */
export default function EventWaitlistCta({
  show,
  comedian,
}: EventWaitlistCtaProps) {
  const { isSubscribed, isLoading } = useWaitlistStatus({
    showId: show.id,
    ownerId: comedian.id,
    isVenue: false,
  });

  if (isLoading) {
    return (
      <button
        disabled
        className="w-full flex items-center justify-center py-3 px-4 rounded-lg font-medium bg-gray-100 text-gray-500 cursor-not-allowed"
      >
        <Loader2 className="animate-spin mr-2" size={20} />
        <span>Checking&hellip;</span>
      </button>
    );
  }

  if (isSubscribed) {
    return (
      <div className="w-full flex items-center justify-center py-3 px-4 rounded-lg font-medium bg-green-500 text-white">
        <CheckCircle className="mr-2" size={20} />
        <span>You&apos;re on the waitlist</span>
      </div>
    );
  }

  return (
    <WaitlistInlineForm
      show={show}
      ownerId={comedian.id}
      isVenue={false}
      comedianSlug={comedian.slug || undefined}
    />
  );
}
